import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, Checkbox } from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import {
  getVisitVisitsVisitIdGet,
  updateActiveDiagnosesDataVisitsVisitIdActiveDiagnosesDataPut,
} from '../../services/api/sdk.gen';
import { useToast } from '../../hooks/useToastContext';
import { contradictionAnchor } from './contradictions/contradictionNavigation';

interface ConditionState {
  checked: boolean;
  not_assessed: boolean;
}

interface M1028Data {
  peripheral_vascular_disease: ConditionState;
  diabetes_mellitus: ConditionState;
  none_of_the_above: ConditionState;
}

const DEFAULT_M1028: M1028Data = {
  peripheral_vascular_disease: { checked: false, not_assessed: false },
  diabetes_mellitus: { checked: false, not_assessed: false },
  none_of_the_above: { checked: false, not_assessed: false },
};

/**
 * Apply M1028 business rules:
 * 1. checked and not_assessed are mutually exclusive per condition
 * 2. Checking PVD or DM (either field) → uncheck none_of_the_above both fields
 * 3. Checking none_of_the_above (either field) → uncheck PVD and DM both fields
 * 4. When BOTH pvd.not_assessed AND dm.not_assessed → auto-check none_of_the_above.not_assessed
 */
function applyM1028Rules(
  data: M1028Data,
  changedCondition: keyof M1028Data
): M1028Data {
  const result = structuredClone(data);

  if (changedCondition === 'none_of_the_above') {
    // Rule 3: none_of_the_above → clear PVD and DM
    if (
      result.none_of_the_above.checked ||
      result.none_of_the_above.not_assessed
    ) {
      result.peripheral_vascular_disease = {
        checked: false,
        not_assessed: false,
      };
      result.diabetes_mellitus = { checked: false, not_assessed: false };
    }
  } else {
    // Rule 2: PVD or DM → clear none_of_the_above
    if (
      result.peripheral_vascular_disease.checked ||
      result.peripheral_vascular_disease.not_assessed ||
      result.diabetes_mellitus.checked ||
      result.diabetes_mellitus.not_assessed
    ) {
      result.none_of_the_above = { checked: false, not_assessed: false };
    }
  }

  // Rule 4: Both PVD NA and DM NA → auto-check none_of_the_above.not_assessed
  if (
    result.peripheral_vascular_disease.not_assessed &&
    result.diabetes_mellitus.not_assessed
  ) {
    result.none_of_the_above.not_assessed = true;
  }

  return result;
}

interface OASISActiveDiagnosesProps {
  visitId: string;
  onSave?: () => void;
}

export const OASISActiveDiagnoses: React.FC<OASISActiveDiagnosesProps> = ({
  visitId,
  onSave,
}) => {
  const { error: showError } = useToast();
  const [data, setData] = useState<M1028Data>(DEFAULT_M1028);
  const [loading, setLoading] = useState(true);
  const prevDataRef = useRef<M1028Data>(DEFAULT_M1028);

  const fetchData = useCallback(async () => {
    try {
      const response = await getVisitVisitsVisitIdGet({
        path: { visit_id: visitId },
      });
      if (response.data?.active_diagnoses_data) {
        const raw = response.data.active_diagnoses_data as Record<
          string,
          unknown
        >;
        const m1028 = (raw.M1028_active_diagnoses ??
          DEFAULT_M1028) as M1028Data;
        setData(m1028);
        prevDataRef.current = m1028;
      }
    } catch (err) {
      console.error('Failed to fetch active diagnoses data:', err);
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveData = useCallback(
    async (newData: M1028Data) => {
      try {
        const response =
          await updateActiveDiagnosesDataVisitsVisitIdActiveDiagnosesDataPut({
            path: { visit_id: visitId },
            body: {
              active_diagnoses_data: { M1028_active_diagnoses: newData },
            },
          });
        if (response.error) {
          showError('Failed to save M1028 data');
          setData(prevDataRef.current);
          return;
        }
        prevDataRef.current = newData;
        onSave?.();
      } catch (err) {
        console.error('Failed to save active diagnoses data:', err);
        showError('Failed to save M1028 data');
        // Rollback
        setData(prevDataRef.current);
      }
    },
    [visitId, showError, onSave]
  );

  const handleChange = (
    condition: keyof M1028Data,
    field: 'checked' | 'not_assessed',
    value: boolean
  ) => {
    setData(prev => {
      const updated = structuredClone(prev);

      // Rule 1: Mutual exclusivity within a condition
      if (field === 'checked' && value) {
        updated[condition].not_assessed = false;
      } else if (field === 'not_assessed' && value) {
        updated[condition].checked = false;
      }
      updated[condition][field] = value;

      const result = applyM1028Rules(updated, condition);
      saveData(result);
      return result;
    });
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <Loader2 className='w-5 h-5 animate-spin text-muted-foreground' />
      </div>
    );
  }

  const noneAutoLocked =
    data.peripheral_vascular_disease.not_assessed &&
    data.diabetes_mellitus.not_assessed;

  return (
    <Card>
      <CardContent className='pt-6'>
        <div className='text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-4'>
          M1028. Active Diagnoses - Comorbidities and Co-existing Conditions
        </div>

        {/* Field-less module anchor: the contradiction navigator's last-resort tier
            matches [data-contradiction-source]:not([data-contradiction-field]), so a
            finding whose named checkbox isn't rendered still lands inside M1028. */}
        <div className='space-y-3' {...contradictionAnchor('active_diagnoses')}>
          {/* Peripheral Vascular Disease */}
          <ConditionRow
            fieldKey='peripheral_vascular_disease'
            label='Peripheral Vascular Disease (PVD) or Peripheral Arterial Disease (PAD)'
            checked={data.peripheral_vascular_disease.checked}
            notAssessed={data.peripheral_vascular_disease.not_assessed}
            onCheckedChange={v =>
              handleChange('peripheral_vascular_disease', 'checked', v)
            }
            onNotAssessedChange={v =>
              handleChange('peripheral_vascular_disease', 'not_assessed', v)
            }
          />

          {/* Diabetes Mellitus */}
          <ConditionRow
            fieldKey='diabetes_mellitus'
            label='Diabetes Mellitus (DM)'
            checked={data.diabetes_mellitus.checked}
            notAssessed={data.diabetes_mellitus.not_assessed}
            onCheckedChange={v =>
              handleChange('diabetes_mellitus', 'checked', v)
            }
            onNotAssessedChange={v =>
              handleChange('diabetes_mellitus', 'not_assessed', v)
            }
          />

          {/* None of the above */}
          <ConditionRow
            fieldKey='none_of_the_above'
            label='None of the above'
            checked={data.none_of_the_above.checked}
            notAssessed={data.none_of_the_above.not_assessed}
            notAssessedDisabled={noneAutoLocked}
            onCheckedChange={v =>
              handleChange('none_of_the_above', 'checked', v)
            }
            onNotAssessedChange={v =>
              handleChange('none_of_the_above', 'not_assessed', v)
            }
          />
        </div>
      </CardContent>
    </Card>
  );
};

interface ConditionRowProps {
  fieldKey: string;
  label: string;
  checked: boolean;
  notAssessed: boolean;
  notAssessedDisabled?: boolean;
  onCheckedChange: (value: boolean) => void;
  onNotAssessedChange: (value: boolean) => void;
}

const ConditionRow: React.FC<ConditionRowProps> = ({
  fieldKey,
  label,
  checked,
  notAssessed,
  notAssessedDisabled,
  onCheckedChange,
  onNotAssessedChange,
}) => (
  <div
    className='flex items-center justify-between gap-4 py-2 border-b border-gray-100 last:border-0'
    {...contradictionAnchor('active_diagnoses', fieldKey)}
  >
    <div className='flex items-center gap-3 flex-1 min-w-0'>
      <Checkbox
        checked={checked}
        onCheckedChange={v => onCheckedChange(v === true)}
      />
      <span className='text-sm text-gray-700'>{label}</span>
    </div>
    <div className='flex items-center gap-2 shrink-0'>
      <Checkbox
        checked={notAssessed}
        disabled={notAssessedDisabled}
        onCheckedChange={v => onNotAssessedChange(v === true)}
      />
      <span className='text-xs text-muted-foreground whitespace-nowrap'>
        Not assessed / No info
      </span>
    </div>
  </div>
);

export default OASISActiveDiagnoses;
